/**
 * 顧客詳細「商談」タブ — 発行した書類 (2026-10)。
 *
 * 見積 / 請求 / 発注 / 発送 を 1 つの一覧で表示する。
 * - 状態は日本語ラベル (旧: won / paid / pending の英語コード表示)
 * - 発注の金額は「送金額 (メーカー通貨)」が確定していればそれを、無ければ仕入合計 (0 円は非表示)
 * - 各行から開く: 見積 = 見積詳細 / 請求 = 請求書ページ (公開トークンがある場合) / 発注 = 商談詳細の該当発注カード / 発送 = 商談詳細「請求・発注・発送」タブ
 * - 商談詳細を開く時は戻り先 (顧客詳細) を渡す。商談詳細の「前の画面へ」でこの画面に戻れる (2026-10-30)
 */
import type {
  DeliveryStatus,
  InvoiceStatus,
  IsoOrTimestamp,
  PurchaseOrderStatus,
  QuoteStatus,
} from '@goenhub/shared';
import { formatMoney } from '@goenhub/shared';
import { useNavigate } from 'react-router-dom';

import { DocumentQuickActions } from './DocumentQuickActions';

import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useDealQuotes } from '@/hooks/useQuotes';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useDealDeliveries, useDealInvoices, useDealPurchaseOrders } from '@/hooks/useSalesOps';
import { paths } from '@/routes/paths';

type Variant = NonNullable<BadgeProps['variant']>;

const QUOTE_STATUS: Record<QuoteStatus, [string, Variant]> = {
  draft: ['下書き', 'draft'],
  sent: ['送信済', 'sent'],
  expired: ['期限切れ', 'default'],
  won: ['成約', 'approved'],
  lost: ['失注', 'danger'],
};
const INVOICE_STATUS: Record<InvoiceStatus, [string, Variant]> = {
  unpaid: ['未入金', 'draft'],
  paid: ['入金済', 'approved'],
  overdue: ['期限超過', 'danger'],
  cancelled: ['キャンセル', 'default'],
};
const PO_STATUS: Record<PurchaseOrderStatus, [string, Variant]> = {
  pending: ['未発注', 'draft'],
  ordered: ['発注済（入荷待ち）', 'sent'],
  received: ['入荷済（検品待ち）', 'review'],
  inspected: ['検品済（発送待ち）', 'approved'],
  shipped: ['発送済', 'default'],
  cancelled: ['キャンセル', 'default'],
};
const DELIVERY_STATUS: Record<DeliveryStatus, [string, Variant]> = {
  pending: ['準備中', 'draft'],
  in_transit: ['配送中', 'sent'],
  completed: ['完了', 'approved'],
  cancelled: ['キャンセル', 'default'],
};

interface Row {
  key: string;
  kind: string;
  title: string;
  status: [string, Variant];
  amount: string;
  date: IsoOrTimestamp | null;
  actionLabel: string;
  onAction: () => void;
}

interface Props {
  dealId: string;
  /** メーカー取引通貨 (主軸カテゴリーの manufacturerCurrency)。発注の送金額表示に使う。 */
  currency: string;
  onCreateQuote: () => void;
}

export function DealIssuedDocuments({ dealId, currency, onCreateQuote }: Props) {
  const navigate = useNavigate();
  const returnToState = useReturnToState('顧客詳細');
  const quotes = useDealQuotes(dealId);
  const invoices = useDealInvoices(dealId);
  const purchaseOrders = useDealPurchaseOrders(dealId);
  const deliveries = useDealDeliveries(dealId);

  const isLoading =
    quotes.isLoading || invoices.isLoading || purchaseOrders.isLoading || deliveries.isLoading;
  const fulfillmentUrl = `${paths.dealDetail.replace(':id', dealId)}?tab=fulfillment`;
  const openDeal = (url: string) => navigate(url, { state: returnToState });

  const rows: Row[] = [
    ...(quotes.data ?? []).map((q) => ({
      key: `q-${q.id}`,
      kind: '見積書',
      title: q.title || `見積 ${q.id.slice(0, 6)}`,
      status: QUOTE_STATUS[q.status] ?? [q.status, 'default' as Variant],
      amount: yen(q.totalIncludingTax),
      date: q.issuedAt,
      actionLabel: '開く',
      onAction: () =>
        navigate(paths.quoteDetail.replace(':dealId', dealId).replace(':quoteId', q.id)),
    })),
    ...(invoices.data ?? []).map((iv) => ({
      key: `i-${iv.id}`,
      kind: '請求書',
      title: iv.invoiceNumber,
      status: INVOICE_STATUS[iv.status] ?? [iv.status, 'default' as Variant],
      amount: yen(iv.amount),
      date: iv.issuedAt,
      actionLabel: '開く',
      onAction: () => {
        if (iv.publicToken) {
          window.open(
            `${window.location.origin}${paths.publicInvoice.replace(':token', iv.publicToken)}`,
            '_blank',
          );
        } else {
          openDeal(fulfillmentUrl);
        }
      },
    })),
    ...(purchaseOrders.data ?? []).map((po) => ({
      key: `p-${po.id}`,
      kind: '発注',
      title: po.poNumber,
      status: PO_STATUS[po.status] ?? [po.status, 'default' as Variant],
      amount:
        po.manufacturerTotalAmount != null
          ? `送金額 ${formatMoney(po.manufacturerTotalAmount - (po.discountAmount ?? 0), currency)}`
          : po.totalCostPrice > 0
            ? yen(po.totalCostPrice)
            : '',
      date: po.createdAt,
      actionLabel: '発注カード',
      onAction: () => openDeal(`${fulfillmentUrl}&po=${po.id}`),
    })),
    ...(deliveries.data ?? []).map((dl) => ({
      key: `d-${dl.id}`,
      kind: '発送',
      title: dl.deliveryNumber,
      status: DELIVERY_STATUS[dl.status] ?? [dl.status, 'default' as Variant],
      amount: '',
      date: dl.shippedAt ?? dl.createdAt,
      actionLabel: '開く',
      onAction: () => openDeal(fulfillmentUrl),
    })),
  ];

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2 pb-1.5 border-b border-[var(--border)]">
        <span className="text-[11px] font-semibold text-[var(--text-secondary)]">
          発行した書類（{rows.length}）
        </span>
        <DocumentQuickActions
          dealId={dealId}
          onCreateQuote={onCreateQuote}
          onJumpFulfillment={() => openDeal(fulfillmentUrl)}
        />
      </div>
      {isLoading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] py-1.5">
          発行した書類はありません。「＋ 書類作成」から見積書を作成できます。
        </p>
      ) : (
        <div className="divide-y divide-[var(--border)]">
          {rows.map((r) => (
            <div key={r.key} className="flex flex-wrap items-center gap-2 py-2 text-xs">
              <span className="inline-flex justify-center min-w-[44px] h-5 items-center px-1.5 rounded border border-[var(--border)] text-[10px] text-[var(--text-secondary)]">
                {r.kind}
              </span>
              <span className="flex-1 min-w-[140px] truncate font-medium text-[var(--text-primary)]">
                {r.title}
              </span>
              <Badge variant={r.status[1]}>{r.status[0]}</Badge>
              {r.amount && (
                <span className="tabular-nums text-[11px] text-[var(--text-secondary)]">
                  {r.amount}
                </span>
              )}
              <span className="text-[11px] text-[var(--text-muted)] tabular-nums">
                {fmtDay(r.date)}
              </span>
              <Button variant="secondary" size="sm" onClick={r.onAction}>
                {r.actionLabel}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function yen(v: number | null | undefined): string {
  if (v === null || v === undefined) return '';
  return `¥${v.toLocaleString('ja-JP')}`;
}

function fmtDay(v: IsoOrTimestamp | null | undefined): string {
  if (!v) return '';
  const d =
    typeof v === 'string'
      ? new Date(v)
      : typeof v === 'object' && 'toDate' in v
        ? (v as { toDate: () => Date }).toDate()
        : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' });
}
